import { FormInstance } from "antd";
import { useAppDispatch } from "../../../store";
import { useOpenProgressModal } from "../../../lib/hooks/useOpenProgressModal";
import { ReactNode, useCallback, useState } from "react";
import { BULK_EMAILS_WERE_CREATED, SEND_SCHEDULED_SUCCESS } from "../../../lib/constants";
import { setErrorMessage } from "../../../acions/actionsCreators/messageActions";
import { MailingsBulkCreationService } from "../../../services/mailingsBulkCreation";
import { useFormData } from "./useFormData";
import { useConfirmationModal } from "./useConfirmationModal";
import { getErrorMessages } from "../../../lib/getErrorMessages";

export const useSend = (form: FormInstance, count: number, onSuccess: () => void) => {
  const dispatch = useAppDispatch();

  const { getFormData } = useFormData();

  const [isSending, setIsSending] = useState<boolean>(false);

  const { openModal: openEmailModal } = useOpenProgressModal(
    "Creating emails",
    "Creating emails failed",
    () => {
      onSuccess();
    },
    {
      customMsg: BULK_EMAILS_WERE_CREATED,
      closable: false
    }
  );

  const { openModal: openScheduleModal } = useOpenProgressModal(
    "Creating emails",
    "Creating emails failed",
    () => {
      onSuccess();
    },
    {
      customMsg: SEND_SCHEDULED_SUCCESS,
      closable: false
    }
  );

  const getOnError = (error: any): string | ReactNode => {
    const keys: string[] = Object.keys(error.response.data.errors || {});

    if (keys.length > 0) {
      return getErrorMessages(error, "Mailings creation error");
    } else {
      return error.response.data.title;
    }
  };

  const sendInternal = useCallback(
    async (formData: any) => {
      try {
        setIsSending(true);
        const id: string = await MailingsBulkCreationService.add(formData);
        if (!formData.scheduleRules) {
          openEmailModal(id);
        } else {
          openScheduleModal(id);
        }
      } catch (error: any) {
        dispatch(setErrorMessage(getOnError(error), true));
      } finally {
        setIsSending(false);
      }
    },
    [dispatch, openEmailModal, openScheduleModal]
  );

  const { openModal: openConfirmationModal } = useConfirmationModal(count, sendInternal);

  const send = useCallback(
    async (scheduleValues?: any) => {
      try {
        const values = await form.validateFields();
        const formData = getFormData(values, scheduleValues);

        if (!formData.scheduleRules) {
          openConfirmationModal(formData);
        } else {
          await sendInternal(formData);
        }
      } catch (error: any) {
        if (error?.errorFields) {
          return;
        }
        dispatch(setErrorMessage(error.response.data.title, true));
      }
    },
    [dispatch, form, getFormData, openConfirmationModal, sendInternal]
  );

  return { send, isSending };
};
